import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Button,
  InputAdornment,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useNotifier } from "../../../Core/Notifier";
import { useCreateCompanyMutation } from "../../../Api/attoDeskApi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadImage } from "../../../Components/UploadFile/upload";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  validateEmail,
  validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const formatPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const fieldLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  letterSpacing: 0.4,
  mb: 0.75,
  display: "block",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: "#f8fafc",
    transition: "all 0.2s",
    "&:hover": { bgcolor: "#f1f5f9" },
    "&.Mui-focused": {
      bgcolor: "#ffffff",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb", borderWidth: 2 },
    },
  },
};

// Section header with coloured accent line
const SectionHeading = ({
  icon,
  title,
  subtitle,
  color = "#2563eb",
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color?: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        bgcolor: color + "18",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      <Box sx={{ color }}>{icon}</Box>
    </Box>
    <Box sx={{ borderLeft: `3px solid ${color}`, pl: 1.5 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>{subtitle}</Typography>
    </Box>
  </Box>
);

// Image upload zone
const ImageUploadZone = ({
  id,
  label,
  description,
  preview,
  accentColor,
  onChange,
  onClear,
}: {
  id: string;
  label: string;
  description: string;
  preview?: string;
  accentColor: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}) => (
  <Box>
    <Typography sx={fieldLabel}>{label}</Typography>
    {preview ? (
      <Box
        sx={{
          borderRadius: 2.5,
          border: "2px solid " + accentColor + "40",
          bgcolor: accentColor + "08",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <img
          src={preview}
          alt="preview"
          style={{ maxHeight: 80, maxWidth: "100%", objectFit: "contain" }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
          onClick={onClear}
          sx={{
            fontSize: 11,
            borderColor: "#fca5a5",
            color: "#dc2626",
            borderRadius: 1.5,
            py: 0.4,
            "&:hover": { bgcolor: "#fef2f2", borderColor: "#f87171" },
          }}
        >
          Remove
        </Button>
      </Box>
    ) : (
      <label htmlFor={id} style={{ display: "block", cursor: "pointer" }}>
        <Box
          sx={{
            borderRadius: 2.5,
            border: "2px dashed #e2e8f0",
            bgcolor: "#f8fafc",
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            transition: "all 0.2s",
            "&:hover": {
              borderColor: accentColor,
              bgcolor: accentColor + "08",
              "& .upload-icon": { transform: "translateY(-2px)" },
            },
          }}
        >
          <Box
            className="upload-icon"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: accentColor + "18",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.2s",
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 22, color: accentColor }} />
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
            Click to upload
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
            {description}
          </Typography>
        </Box>
      </label>
    )}
    <input accept="image/*" style={{ display: "none" }} id={id} type="file" onChange={onChange} />
  </Box>
);

const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [selectedWatermarkImage, setSelectedWatermarkImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [newCompany, { isLoading }] = useCreateCompanyMutation();

  const formik = useFormik({
    initialValues: {
      companyName: "",
      companySlog: "",
      companyProvince: "",
      companyCity: "",
      companyPostalCode: "",
      companyStreetNumber: "",
      companyStreetName: "",
      companyPaymentMethod: "",
      companyEmail: "",
      companyMobileNumber: "",
      companyAltMobileNumber: "",
      companyWebSiteLink: "",
      companyFacebookLink: "",
      companyInstagramLink: "",
      companyHstNo: "",
      companyTripPrefix: "",
      companyDescription: "",
      companyTermsAndConditions: "",
      companyCancellationPolicy: "",
      companyFooterDetails: "",
      CompanyLogo: "",
      CompanyWatermark: "",
      isDefault: false,
      companyDocumentTemplate: "charter",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.companyName) errors.companyName = "Company name is required";
      const emailError = validateEmail(values.companyEmail);
      if (emailError) errors.companyEmail = emailError;
      const mobileError = validatePhoneNumber(values.companyMobileNumber.replace(/\D/g, ""));
      if (mobileError) errors.companyMobileNumber = mobileError;
      const altError = validatePhoneNumber(values.companyAltMobileNumber.replace(/\D/g, ""));
      if (altError) errors.companyAltMobileNumber = altError;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let CompanyLogo: string | undefined;
        if (selectedImage) {
          setIsImageUploading(true);
          CompanyLogo = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        let CompanyWatermark: string | undefined;
        if (selectedWatermarkImage) {
          setIsImageUploading(true);
          CompanyWatermark = (await uploadImage(selectedWatermarkImage)) || "";
          setIsImageUploading(false);
        }
        const response = await newCompany({
          companyName: values.companyName,
          companySlog: values.companySlog,
          companyStreetNumber: values.companyStreetNumber,
          companyStreetName: values.companyStreetName,
          companyCity: values.companyCity,
          companyProvince: values.companyProvince,
          companyPostalCode: values.companyPostalCode,
          companyPaymentMethod: values.companyPaymentMethod,
          companyEmail: values.companyEmail,
          companyMobileNumber: values.companyMobileNumber,
          companyAltMobileNumber: values.companyAltMobileNumber,
          companyWebSiteLink: values.companyWebSiteLink,
          companyFacebookLink: values.companyFacebookLink,
          companyInstagramLink: values.companyInstagramLink,
          companyHstNo: values.companyHstNo,
          companyTripPrefix: values.companyTripPrefix.toUpperCase().slice(0, 5),
          companyDescription: values.companyDescription,
          companyTermsAndConditions: values.companyTermsAndConditions,
          companyCancellationPolicy: values.companyCancellationPolicy,
          companyFooterDetails: values.companyFooterDetails,
          isDefault: values.isDefault,
          CompanyLogo,
          CompanyWatermark,
          companyDocumentTemplate: values.companyDocumentTemplate,
        }).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          resetForm();
          setSelectedImage(undefined);
          setSelectedWatermarkImage(undefined);
          navigate("/company/list");
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (ev) => {
        const img = new Image();
        img.src = ev.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let { width: w, height: h } = img;
          if (w > h) { if (w > maxW) { h = (h * maxW) / w; w = maxW; } }
          else { if (h > maxH) { w = (w * maxH) / h; h = maxH; } }
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d")?.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => blob ? resolve(new File([blob], file.name, { type: file.type })) : reject(new Error("Empty")),
            file.type
          );
        };
        img.onerror = reject;
      };
    });

  const handleFile = async (file: File, setter: (f: File | undefined) => void) => {
    const valid = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
    if (!valid.includes(file.type)) { showErrorMessage("Must be jpeg, png, jpg, gif or webp."); setter(undefined); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("File must be smaller than 2 MB."); setter(undefined); return; }
    try { setter(await resizeImage(file, 500, 500)); }
    catch { showErrorMessage("Failed to process image."); setter(undefined); }
  };

  const cancel = () => {
    formik.resetForm();
    setSelectedImage(undefined);
    setSelectedWatermarkImage(undefined);
    navigate("/company/list");
  };

  if (isLoading || isImageUploading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="40vh" flexDirection="column" gap={2}>
        <CircularProgress size={36} sx={{ color: "#2563eb" }} />
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
          {isImageUploading ? "Uploading images..." : "Saving company..."}
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>
      <form onSubmit={formik.handleSubmit}>

        {/* ── Hero Header ── */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            px: { xs: 2, md: 4 },
            py: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                background: "linear-gradient(135deg, #2563eb, #2f3891)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(37,99,235,0.4)",
              }}
            >
              <BusinessIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#ffffff", lineHeight: 1.2 }}>
                New Company
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>
                Fill in the details to register your company
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={cancel}
              sx={{
                borderColor: "rgba(255,255,255,0.25)",
                color: "rgba(255,255,255,0.8)",
                borderRadius: 2,
                "&:hover": { borderColor: "rgba(255,255,255,0.5)", bgcolor: "rgba(255,255,255,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveAltIcon />}
              onClick={() => formik.handleSubmit()}
              sx={{
                background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                borderRadius: 2,
                fontWeight: 700,
                boxShadow: "0 4px 12px rgba(37,99,235,0.4)",
                "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #1e40af)", boxShadow: "0 6px 16px rgba(37,99,235,0.5)" },
              }}
            >
              Save Company
            </Button>
          </Box>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          <Grid container spacing={3}>

            {/* ── LEFT COLUMN ── */}
            <Grid item xs={12} lg={8}>

              {/* Company Details */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  mb: 3,
                  overflow: "hidden",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                }}
              >
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading
                    icon={<BusinessIcon sx={{ fontSize: 20 }} />}
                    title="Company Details"
                    subtitle="Basic identity and registration information"
                    color="#2563eb"
                  />
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={8}>
                      <Typography sx={fieldLabel}>Company Name *</Typography>
                      <TextField
                        size="small" fullWidth placeholder="e.g. TJ Bus Lines Inc."
                        {...formik.getFieldProps("companyName")}
                        error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                        helperText={formik.touched.companyName && formik.errors.companyName}
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography sx={fieldLabel}>HST Number</Typography>
                      <TextField size="small" fullWidth placeholder="123456789 RT0001"
                        {...formik.getFieldProps("companyHstNo")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Typography sx={fieldLabel}>Trip Prefix</Typography>
                      <TextField size="small" fullWidth placeholder="TJT"
                        inputProps={{ maxLength: 5, style: { textTransform: "uppercase", letterSpacing: 2, fontWeight: 700 } }}
                        {...formik.getFieldProps("companyTripPrefix")}
                        onChange={(e) => formik.setFieldValue("companyTripPrefix", e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
                        sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <Typography sx={fieldLabel}>Slogan</Typography>
                      <TextField size="small" fullWidth placeholder="e.g. Ride with comfort"
                        {...formik.getFieldProps("companySlog")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabel}>Website</Typography>
                      <TextField
                        size="small" fullWidth placeholder="https://www.example.com"
                        {...formik.getFieldProps("companyWebSiteLink")}
                        sx={inputSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>🌐</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    {/* Sits with Website because all three are public contact
                        links, and all three feed the shared email footer. */}
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Facebook</Typography>
                      <TextField
                        size="small" fullWidth placeholder="https://facebook.com/yourpage"
                        {...formik.getFieldProps("companyFacebookLink")}
                        sx={inputSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>📘</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Instagram</Typography>
                      <TextField
                        size="small" fullWidth placeholder="https://instagram.com/yourpage"
                        {...formik.getFieldProps("companyInstagramLink")}
                        sx={inputSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>📷</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={fieldLabel}>Description</Typography>
                      <TextField
                        size="small" fullWidth multiline rows={3}
                        placeholder="Brief overview of your company..."
                        {...formik.getFieldProps("companyDescription")}
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderRadius: 2, bgcolor: "#eff6ff", border: "1px solid #bfdbfe" }}>
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e40af" }}>
                            Set as Default Company
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "#3b82f6", mt: 0.25 }}>
                            This company will appear pre-selected on new quotes
                          </Typography>
                        </Box>
                        <CustomSwitch color="primary" formik={formik} name="isDefault" />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {/* Contact Details */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  mb: 3,
                  overflow: "hidden",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                }}
              >
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading
                    icon={<ContactPhoneIcon sx={{ fontSize: 20 }} />}
                    title="Contact Details"
                    subtitle="How clients and staff can reach you"
                    color="#0891b2"
                  />
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Email Address</Typography>
                      <TextField
                        size="small" fullWidth placeholder="info@company.com"
                        {...formik.getFieldProps("companyEmail")}
                        error={formik.touched.companyEmail && Boolean(formik.errors.companyEmail)}
                        helperText={formik.touched.companyEmail && formik.errors.companyEmail}
                        sx={inputSx}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: 14, color: "#94a3b8" }}>✉️</Typography></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Phone Number</Typography>
                      <TextField
                        size="small" fullWidth placeholder="(XXX) XXX-XXXX"
                        value={formik.values.companyMobileNumber}
                        onChange={(e) => formik.setFieldValue("companyMobileNumber", formatPhone(e.target.value))}
                        onBlur={() => formik.setFieldTouched("companyMobileNumber", true)}
                        error={formik.touched.companyMobileNumber && Boolean(formik.errors.companyMobileNumber)}
                        helperText={formik.touched.companyMobileNumber && formik.errors.companyMobileNumber}
                        inputProps={{ maxLength: 14 }}
                        sx={inputSx}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: 14, color: "#94a3b8" }}>📞</Typography></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Alternate Phone</Typography>
                      <TextField
                        size="small" fullWidth placeholder="(XXX) XXX-XXXX"
                        value={formik.values.companyAltMobileNumber}
                        onChange={(e) => formik.setFieldValue("companyAltMobileNumber", formatPhone(e.target.value))}
                        onBlur={() => formik.setFieldTouched("companyAltMobileNumber", true)}
                        error={formik.touched.companyAltMobileNumber && Boolean(formik.errors.companyAltMobileNumber)}
                        helperText={formik.touched.companyAltMobileNumber && formik.errors.companyAltMobileNumber}
                        inputProps={{ maxLength: 14 }}
                        sx={inputSx}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: 14, color: "#94a3b8" }}>📱</Typography></InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {/* Address */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  mb: 3,
                  overflow: "hidden",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                }}
              >
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading
                    icon={<LocationOnIcon sx={{ fontSize: 20 }} />}
                    title="Address"
                    subtitle="Physical location of your business"
                    color="#16a34a"
                  />
                  <Grid container spacing={2.5}>
                    <Grid item xs={4} sm={2}>
                      <Typography sx={fieldLabel}>St. No.</Typography>
                      <TextField size="small" fullWidth placeholder="123"
                        {...formik.getFieldProps("companyStreetNumber")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={8} sm={5}>
                      <Typography sx={fieldLabel}>Street Name</Typography>
                      <TextField size="small" fullWidth placeholder="Main Street"
                        {...formik.getFieldProps("companyStreetName")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Typography sx={fieldLabel}>City</Typography>
                      <TextField size="small" fullWidth placeholder="Toronto"
                        {...formik.getFieldProps("companyCity")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Province</Typography>
                      <TextField size="small" fullWidth placeholder="Ontario"
                        {...formik.getFieldProps("companyProvince")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Postal Code</Typography>
                      <TextField size="small" fullWidth placeholder="M1A 1A1"
                        {...formik.getFieldProps("companyPostalCode")} sx={inputSx} />
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {/* Documents */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  mb: 3,
                  overflow: "hidden",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                }}
              >
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading
                    icon={<DescriptionIcon sx={{ fontSize: 20 }} />}
                    title="Invoice & Documents"
                    subtitle="Text that appears on invoices and client documents"
                    color="#2f3891"
                  />
                  <Grid container spacing={2.5}>

<Grid item xs={12}>
  <Typography sx={fieldLabel}>Document Template</Typography>
  <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
    {[
      {
        value: "charter",
        label: "Charter Confirmation",
        description: "School bus / trip style",
        icon: "🚌",
        color: "#2563eb",
        bg: "#dbeafe",
      },
      {
        value: "invoice",
        label: "Invoice",
        description: "Coach bus / corporate style",
        icon: "📄",
        color: "#16a34a",
        bg: "#dcfce7",
      },
    ].map((tpl) => {
      const isSelected = formik.values.companyDocumentTemplate === tpl.value;
      return (
        <Box
          key={tpl.value}
          onClick={() => formik.setFieldValue("companyDocumentTemplate", tpl.value)}
          sx={{
            flex: 1,
            border: `2px solid ${isSelected ? tpl.color : "#e2e8f0"}`,
            borderRadius: 2,
            p: 2,
            cursor: "pointer",
            bgcolor: isSelected ? tpl.bg : "#f8fafc",
            transition: "all 0.2s",
            "&:hover": { borderColor: tpl.color, bgcolor: tpl.bg },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 18 }}>{tpl.icon}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: isSelected ? tpl.color : "#334155" }}>
              {tpl.label}
            </Typography>
            {isSelected && (
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: tpl.color, ml: "auto" }} />
            )}
          </Box>
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>
            {tpl.description}
          </Typography>
        </Box>
      );
    })}
  </Box>
</Grid>


                    <Grid item xs={12} md={6}>
                      <Typography sx={fieldLabel}>Terms & Conditions</Typography>
                      <TextField size="small" fullWidth multiline rows={6}
                        placeholder="Enter your terms and conditions..."
                        {...formik.getFieldProps("companyTermsAndConditions")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography sx={fieldLabel}>Cancellation Policy</Typography>
                      <TextField size="small" fullWidth multiline rows={6}
                        placeholder="Enter your cancellation policy..."
                        {...formik.getFieldProps("companyCancellationPolicy")} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={fieldLabel}>Invoice Footer</Typography>
                      <TextField size="small" fullWidth multiline rows={2}
                        placeholder="Text shown at the bottom of every invoice..."
                        {...formik.getFieldProps("companyFooterDetails")} sx={inputSx} />
                    </Grid>
                  </Grid>
                </Box>
              </Box>

            </Grid>

            {/* ── RIGHT SIDEBAR ── */}
            <Grid item xs={12} lg={4}>

              {/* Branding Card */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  mb: 3,
                  overflow: "hidden",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                  position: { lg: "sticky" },
                  top: { lg: 24 },
                }}
              >
                {/* Card accent header */}
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#ffffff", letterSpacing: 0.6, textTransform: "uppercase" }}>
                    Branding
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                    Logo & watermark for invoices
                  </Typography>
                </Box>

                <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
                  <ImageUploadZone
                    id="logo-upload"
                    label="Company Logo"
                    description="PNG, JPG, WEBP · Max 2 MB"
                    accentColor="#2563eb"
                    preview={selectedImage ? URL.createObjectURL(selectedImage) : undefined}
                    onChange={async (e) => { const f = e.target.files?.[0]; if (f) await handleFile(f, setSelectedImage); else setSelectedImage(undefined); }}
                    onClear={() => setSelectedImage(undefined)}
                  />
                  <ImageUploadZone
                    id="watermark-upload"
                    label="Watermark Image"
                    description="Shown as background on invoices"
                    accentColor="#2f3891"
                    preview={selectedWatermarkImage ? URL.createObjectURL(selectedWatermarkImage) : undefined}
                    onChange={async (e) => { const f = e.target.files?.[0]; if (f) await handleFile(f, setSelectedWatermarkImage); else setSelectedWatermarkImage(undefined); }}
                    onClear={() => setSelectedWatermarkImage(undefined)}
                  />
                </Box>

                {/* Tips */}
                <Box sx={{ mx: 3, mb: 3, p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, mb: 1 }}>
                    Tips
                  </Typography>
                  {[
                    "Use a transparent PNG for the best logo display",
                    "Square logos (1:1 ratio) work best",
                    "Watermarks should be light/faded images",
                  ].map((tip) => (
                    <Box key={tip} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.75 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#22c55e", mt: 0.25, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>{tip}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Save button inside sidebar too */}
                <Box sx={{ px: 3, pb: 3 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<SaveAltIcon />}
                    onClick={() => formik.handleSubmit()}
                    sx={{
                      background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                      borderRadius: 2,
                      py: 1.25,
                      fontWeight: 700,
                      fontSize: 14,
                      boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
                      "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #1e40af)" },
                    }}
                  >
                    Save Company
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={cancel}
                    sx={{
                      mt: 1, borderRadius: 2, borderColor: "#e2e8f0",
                      color: "#64748b", fontWeight: 600,
                      "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>

            </Grid>
          </Grid>
        </Box>
      </form>
    </Box>
  );
};

export default Index;
